import React, { useEffect, useState } from "react";
import { Plus, X, Trash2, Edit2, Save } from "lucide-react";
import { api, fetchCollections, fetchTags } from "../../lib/api";
import { ImageUpload } from "../../components/ImageUpload";

const blank = {
  slug: "",
  name: "",
  name_pt: "",
  description: "",
  description_pt: "",
  price: 30,
  cost: 0,
  collection: "wrestling",
  artist: "",
  images: [],
  sizes: ["S", "M", "L", "XL"],
  colors: ["Black"],
  badge: "",
  badge_pt: "",
  in_stock: true,
  tags: [],
  artist_royalty_percent: 30,
};

const TAG_COLOR_MAP = {
  yellow: "bg-kult-yellow text-black",
  pink: "bg-kult-pink text-black",
  white: "bg-white text-black",
  black: "bg-black text-white border border-white",
};

export const AdminProductsTab = ({ products, artistOptions, refresh }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingSlug, setEditingSlug] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [collectionOpts, setCollectionOpts] = useState([
    { v: "wrestling", l: "WRESTLING" },
    { v: "music", l: "MUSIC" },
    { v: "events", l: "EVENTS" },
  ]);
  const [tagOpts, setTagOpts] = useState([]);

  useEffect(() => {
    fetchCollections()
      .then((d) => {
        const opts = (d.collections || [])
          .filter((c) => c.slug !== "all")
          .map((c) => ({ v: c.slug, l: (c.name_en || c.slug).toUpperCase() }));
        if (opts.length) setCollectionOpts(opts);
      })
      .catch(() => {});
    fetchTags().then((d) => setTagOpts(d.tags || [])).catch(() => {});
  }, []);

  const startEdit = (p) => {
    setEditingSlug(p.slug);
    setForm({
      slug: p.slug,
      name: p.name || "",
      name_pt: p.name_pt || "",
      description: p.description || "",
      description_pt: p.description_pt || "",
      price: p.price,
      cost: p.cost ?? 0,
      collection: p.collection,
      artist: p.artist || "",
      images: p.images || [],
      sizes: p.sizes || [],
      colors: p.colors || [],
      badge: p.badge || "",
      badge_pt: p.badge_pt || "",
      in_stock: p.in_stock !== false,
      tags: Array.isArray(p.tags) ? p.tags : [],
      artist_royalty_percent: p.artist_royalty_percent != null ? p.artist_royalty_percent : 30,
    });
    setShowCreate(true);
  };

  const reset = () => {
    setForm(blank);
    setEditingSlug(null);
    setShowCreate(false);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        cost: Number(form.cost || 0),
        sizes: typeof form.sizes === "string" ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean) : form.sizes,
        colors: typeof form.colors === "string" ? form.colors.split(",").map((s) => s.trim()).filter(Boolean) : form.colors,
        artist: form.artist || null,
        artist_royalty_percent:
          form.artist_royalty_percent === "" || form.artist_royalty_percent === null
            ? null
            : Number(form.artist_royalty_percent),
      };
      if (editingSlug) {
        const { slug, ...rest } = payload;
        await api.patch(`/admin/products/${editingSlug}`, rest);
      } else {
        await api.post("/admin/products", payload);
      }
      reset();
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (slug) => {
    if (!window.confirm(`Delete product "${slug}"?`)) return;
    try {
      await api.delete(`/admin/products/${slug}`);
      await refresh();
    } catch {
      // ignore
    }
  };

  return (
    <section data-testid="admin-products-section">
      <div className="flex items-center justify-between mb-4">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ PRODUCTS</p>
        <button
          onClick={() => (showCreate ? reset() : setShowCreate(true))}
          className="bg-kult-pink text-black px-5 py-2 font-display text-lg hover:bg-kult-yellow inline-flex items-center gap-1"
          data-testid="admin-new-product-toggle"
        >
          {showCreate ? <X size={16} /> : <Plus size={16} />} {showCreate ? "CLOSE" : "NEW PRODUCT"}
        </button>
      </div>

      {showCreate && (
        <form
          onSubmit={submit}
          className="border-2 border-kult-pink p-6 bg-kult-black2 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="admin-new-product-form"
        >
          <h3 className="md:col-span-2 font-display text-2xl">{editingSlug ? `EDIT · ${editingSlug}` : "NEW PRODUCT"}</h3>
          <Field label="NAME (EN)" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testId="new-prod-name" />
          <Field label="NAME (PT)" value={form.name_pt} onChange={(e) => setForm({ ...form, name_pt: e.target.value })} testId="new-prod-name-pt" />
          {!editingSlug && <Field label="SLUG (auto)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} testId="new-prod-slug" />}
          <Field label="PRICE (€)" type="number" min="0" step="0.5" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} testId="new-prod-price" />
          <Field label="COST (€) — admin only, hidden from public" type="number" min="0" step="0.5" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} testId="new-prod-cost" />
          <Field
            label={`ARTIST ROYALTY % — admin only · default 30 · only used if ARTIST is set`}
            type="number"
            min="0"
            max="100"
            step="1"
            value={form.artist_royalty_percent}
            onChange={(e) => setForm({ ...form, artist_royalty_percent: e.target.value })}
            testId="new-prod-royalty"
          />
          <Select label="COLLECTION" value={form.collection} onChange={(e) => setForm({ ...form, collection: e.target.value })}
            options={collectionOpts}
            testId="new-prod-collection"
          />
          <Select label="ARTIST" value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })}
            options={[{ v: "", l: "— NONE —" }, ...artistOptions.map((n) => ({ v: n, l: n }))]}
            testId="new-prod-artist"
          />
          <Field label="BADGE" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} testId="new-prod-badge" />
          <Field label="SIZES (csv)" value={Array.isArray(form.sizes) ? form.sizes.join(",") : form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} testId="new-prod-sizes" />
          <Field label="COLORS (csv)" value={Array.isArray(form.colors) ? form.colors.join(",") : form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} testId="new-prod-colors" />
          <Textarea label="DESCRIPTION (EN)" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-2" testId="new-prod-description" />
          <Textarea label="DESCRIPTION (PT)" value={form.description_pt} onChange={(e) => setForm({ ...form, description_pt: e.target.value })} className="md:col-span-2" testId="new-prod-description-pt" />
          <div className="md:col-span-2">
            <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">IMAGES — first = FRONT (shown by default), second = BACK (shown on hover)</p>
            <ImageUpload value={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} max={6} testIdPrefix="prod-images" recommendedSize="1500×1500 square OR 1200×1500 portrait — JPG/PNG (transparent OK)" />
          </div>

          <div className="md:col-span-2">
            <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">TAGS (click to toggle — manage labels in TAGS tab)</p>
            <div className="flex flex-wrap gap-2" data-testid="new-prod-tags">
              {tagOpts.length === 0 && (
                <span className="font-mono text-xs text-kult-muted">No tags yet — create some in the TAGS tab.</span>
              )}
              {tagOpts.map((tg) => {
                const on = (form.tags || []).includes(tg.slug);
                return (
                  <button
                    type="button"
                    key={tg.slug}
                    onClick={() => {
                      const cur = new Set(form.tags || []);
                      if (cur.has(tg.slug)) cur.delete(tg.slug); else cur.add(tg.slug);
                      setForm({ ...form, tags: Array.from(cur) });
                    }}
                    className={`font-mono text-[10px] tracking-[0.25em] px-3 py-1.5 border-2 transition-all ${TAG_COLOR_MAP[tg.color] || TAG_COLOR_MAP.yellow} ${on ? "border-kult-pink ring-1 ring-kult-pink" : "border-transparent opacity-50 hover:opacity-100"}`}
                    data-testid={`new-prod-tag-${tg.slug}`}
                  >
                    {tg.label_en}
                  </button>
                );
              })}
            </div>
          </div>
          {error && <p className="md:col-span-2 font-mono text-xs text-kult-pink">{error}</p>}

          {/* Live profit breakdown — admin only */}
          {(() => {
            const price = Number(form.price) || 0;
            const cost = Number(form.cost) || 0;
            const r = Number(form.artist_royalty_percent) || 0;
            const profit = Math.max(0, price - cost);
            const artistCut = form.artist ? (profit * r) / 100 : 0;
            const houseCut = profit - artistCut;
            return (
              <div className="md:col-span-2 border border-kult-border bg-kult-black p-4 font-mono text-xs grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="prod-profit-preview">
                <div><span className="text-kult-muted block tracking-[0.2em] text-[10px]">PROFIT</span><span className="text-kult-yellow text-lg">€{profit.toFixed(2)}</span></div>
                <div><span className="text-kult-muted block tracking-[0.2em] text-[10px]">ARTIST CUT</span><span className="text-kult-pink text-lg">€{artistCut.toFixed(2)}</span></div>
                <div><span className="text-kult-muted block tracking-[0.2em] text-[10px]">HOUSE CUT</span><span className="text-white text-lg">€{houseCut.toFixed(2)}</span></div>
                <div><span className="text-kult-muted block tracking-[0.2em] text-[10px]">MARGIN</span><span className="text-white text-lg">{price > 0 ? Math.round((profit / price) * 100) : 0}%</span></div>
              </div>
            );
          })()}
          <button
            type="submit"
            disabled={submitting}
            className="md:col-span-2 bg-kult-pink text-black font-display text-xl py-3 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="new-prod-submit"
          >
            <Save size={18} /> {submitting ? "..." : editingSlug ? "SAVE CHANGES" : "CREATE PRODUCT"}
          </button>
        </form>
      )}

      <div className="border border-kult-border" data-testid="admin-products-list">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-kult-black2 border-b border-kult-border font-mono text-[10px] tracking-[0.3em] text-kult-muted">
          <span className="col-span-5 md:col-span-4">PRODUCT</span>
          <span className="col-span-3 md:col-span-2">COLLECTION</span>
          <span className="hidden md:block md:col-span-2">ARTIST</span>
          <span className="col-span-2 md:col-span-2 text-right">PRICE</span>
          <span className="col-span-2 md:col-span-2 text-right">ACTION</span>
        </div>
        {products.map((p) => (
          <div key={p.id} className="grid grid-cols-12 gap-3 px-5 py-4 border-b border-kult-border last:border-b-0 items-center" data-testid={`admin-product-${p.slug}`}>
            <div className="col-span-5 md:col-span-4 flex items-center gap-3 min-w-0">
              {p.images?.[0] && <img src={resolveUrl(p.images[0])} alt="" className="w-12 h-12 object-cover grayscale" />}
              <span className="font-display text-base leading-tight truncate">{p.name}</span>
            </div>
            <span className="col-span-3 md:col-span-2 font-mono text-[10px] tracking-[0.25em] text-kult-yellow uppercase">{p.collection}</span>
            <span className="hidden md:block md:col-span-2 font-mono text-[10px] tracking-[0.25em] text-kult-muted">{p.artist || "—"}</span>
            <span className="col-span-2 md:col-span-2 text-right font-mono text-sm">
              €{p.price.toFixed(0)}
              {p.cost ? (
                <span className="block text-[10px] text-kult-yellow">
                  margin €{(p.price - (p.cost || 0)).toFixed(2)}
                </span>
              ) : null}
            </span>
            <div className="col-span-2 md:col-span-2 flex justify-end gap-1">
              <button onClick={() => startEdit(p)} className="p-2 border border-kult-border hover:border-kult-pink hover:text-kult-pink" data-testid={`admin-product-edit-${p.slug}`} aria-label="Edit">
                <Edit2 size={14} />
              </button>
              <button onClick={() => del(p.slug)} className="p-2 border border-kult-border hover:border-kult-pink hover:text-kult-pink" data-testid={`admin-product-delete-${p.slug}`} aria-label="Delete">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

function resolveUrl(u) {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

function Field({ label, testId, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <input {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}
function Textarea({ label, testId, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <textarea rows={3} {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}
function Select({ label, options, testId, ...props }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <select {...props} data-testid={testId} className="w-full bg-kult-black border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
